import { useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Palette, Upload, Trash2, ImageIcon } from 'lucide-react';
import { PRESET_COLORS, DEFAULT_MENU_COLOR, DEFAULT_BUTTON_COLOR } from './constants';

export const AppearanceSection = ({
  menuColor,
  setMenuColor,
  buttonColor,
  setButtonColor,
  sidebarBgImage,
  sidebarBgOpacity,
  setSidebarBgOpacity,
  sidebarBgFile,
  setSidebarBgFile,
  sidebarBgPreview,
  setSidebarBgPreview,
  setSidebarBgImage,
  sidebarBgOffset,
  setSidebarBgOffset
}) => {
  const sidebarBgInputRef = useRef(null);

  const handleSidebarBgSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    setSidebarBgFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setSidebarBgPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveSidebarBg = () => {
    setSidebarBgFile(null);
    setSidebarBgPreview('');
    setSidebarBgImage('');
    if (sidebarBgInputRef.current) sidebarBgInputRef.current.value = '';
  };

  return (
    <Card data-testid="appearance-card">
      <CardHeader>
        <CardTitle className="text-xl font-semibold font-title flex items-center gap-2">
          <Palette className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
          Appearance
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Menu Color */}
        <div className="space-y-4">
          <Label className="text-base">Menu & Header Color</Label>
          <p className="text-sm text-gray-500">Choose a color for the sidebar and mobile header.</p>
          
          {/* Preset Colors */}
          <div className="flex flex-wrap gap-3">
            {PRESET_COLORS.map((color) => (
              <button
                key={color.value}
                type="button"
                onClick={() => setMenuColor(color.value)}
                className={`w-12 h-12 rounded-xl transition-all ${
                  menuColor === color.value 
                    ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' 
                    : 'hover:scale-105'
                }`}
                style={{ backgroundColor: color.value }}
                title={color.name}
                data-testid={`color-preset-${color.name.toLowerCase()}`}
              />
            ))}
          </div>

          {/* Custom Color Picker */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <Label className="text-sm text-gray-600">Custom:</Label>
              <div className="relative">
                <input
                  type="color"
                  value={menuColor}
                  onChange={(e) => setMenuColor(e.target.value)}
                  className="w-12 h-12 rounded-xl cursor-pointer border-2 border-gray-200"
                  data-testid="color-picker-input"
                />
              </div>
            </div>
            <Input
              value={menuColor}
              onChange={(e) => setMenuColor(e.target.value)}
              placeholder="#064E3B"
              className="rounded-xl w-32 font-mono text-sm"
              data-testid="color-hex-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setMenuColor(DEFAULT_MENU_COLOR)}
              className="rounded-lg text-sm"
              data-testid="reset-color-btn"
            >
              Reset
            </Button>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 pt-6">
          {/* Button Color */}
          <div className="space-y-4">
            <Label className="text-base">Primary Button Color</Label>
            <p className="text-sm text-gray-500">Choose a color for action buttons throughout the app.</p>
            
            {/* Preset Colors for Buttons */}
            <div className="flex flex-wrap gap-3">
              {PRESET_COLORS.map((color) => (
                <button
                  key={`btn-${color.value}`}
                  type="button"
                  onClick={() => setButtonColor(color.value)}
                  className={`w-12 h-12 rounded-xl transition-all ${
                    buttonColor === color.value 
                      ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' 
                      : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                  data-testid={`button-color-preset-${color.name.toLowerCase()}`}
                />
              ))}
            </div>

            {/* Custom Color Picker for Buttons */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <Label className="text-sm text-gray-600">Custom:</Label>
                <div className="relative">
                  <input
                    type="color"
                    value={buttonColor}
                    onChange={(e) => setButtonColor(e.target.value)}
                    className="w-12 h-12 rounded-xl cursor-pointer border-2 border-gray-200"
                    data-testid="button-color-picker-input"
                  />
                </div>
              </div>
              <Input
                value={buttonColor}
                onChange={(e) => setButtonColor(e.target.value)}
                placeholder="#064E3B"
                className="rounded-xl w-32 font-mono text-sm"
                data-testid="button-color-hex-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setButtonColor(DEFAULT_BUTTON_COLOR)}
                className="rounded-lg text-sm"
                data-testid="reset-button-color-btn"
              >
                Reset
              </Button>
            </div>

            {/* Button Preview */}
            <div className="flex items-center gap-4 pt-2">
              <Label className="text-sm text-gray-600">Preview:</Label>
              <Button
                type="button"
                className="rounded-xl text-white"
                style={{ backgroundColor: buttonColor }}
              >
                Sample Button
              </Button>
            </div>
          </div>
        </div>

        {/* Divider - Sidebar Background Image */}
        <div className="border-t border-gray-200 pt-6">
          <div className="space-y-4">
            <Label className="text-base">Sidebar Background Image</Label>
            <p className="text-sm text-gray-500">Upload an image to display at the bottom of your sidebar menu. The image fades out towards the top.</p>

            <input
              ref={sidebarBgInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleSidebarBgSelect}
              data-testid="sidebar-bg-file-input"
            />

            {sidebarBgPreview ? (
              <div className="space-y-4">
                {/* Preview thumbnail */}
                <div
                  className="relative w-full h-32 rounded-xl overflow-hidden border border-gray-200"
                  style={{ backgroundColor: menuColor }}
                >
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `url(${sidebarBgPreview})`,
                      backgroundSize: 'cover',
                      backgroundPosition: `center calc(100% + ${sidebarBgOffset}px)`,
                      opacity: sidebarBgOpacity,
                      maskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0) 100%)',
                      WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0) 100%)',
                    }}
                  />
                  <div className="absolute bottom-2 left-3 text-white/70 text-xs z-10">Preview</div>
                </div>

                {/* Opacity slider */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-gray-600">Image Opacity</Label>
                    <span className="text-sm text-gray-500 font-mono">{Math.round(sidebarBgOpacity * 100)}%</span>
                  </div>
                  <Slider
                    value={[sidebarBgOpacity * 100]}
                    onValueChange={([val]) => setSidebarBgOpacity(val / 100)}
                    min={5}
                    max={80}
                    step={1}
                    className="w-full"
                    data-testid="sidebar-bg-opacity-slider"
                  />
                  <p className="text-xs text-gray-400">Lower values keep menu items more legible.</p>
                </div>

                {/* Offset slider */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-gray-600">Vertical Offset</Label>
                    <span className="text-sm text-gray-500 font-mono">{sidebarBgOffset}px</span>
                  </div>
                  <Slider
                    value={[sidebarBgOffset]}
                    onValueChange={([val]) => setSidebarBgOffset(val)}
                    min={0}
                    max={300}
                    step={5}
                    className="w-full"
                    data-testid="sidebar-bg-offset-slider"
                  />
                  <p className="text-xs text-gray-400">Shift the image downward to reposition it.</p>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => sidebarBgInputRef.current?.click()}
                    className="rounded-lg text-sm"
                    data-testid="sidebar-bg-change-btn"
                  >
                    <Upload className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                    Change Image
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRemoveSidebarBg}
                    className="rounded-lg text-sm text-red-600 hover:text-red-700 hover:border-red-300"
                    data-testid="sidebar-bg-remove-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => sidebarBgInputRef.current?.click()}
                className="w-full h-28 rounded-xl border-2 border-dashed border-gray-300 hover:border-gray-400 flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-gray-500 transition-colors"
                data-testid="sidebar-bg-upload-btn"
              >
                <ImageIcon className="w-8 h-8" strokeWidth={1.5} />
                <span className="text-sm">Click to upload an image</span>
              </button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AppearanceSection;
